import Menu, { MenuProps } from "@material-ui/core/Menu";
import MenuItem, { MenuItemProps } from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core/styles";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import clsx from "clsx";
import * as React from "react";

const TRANSPARENT = "rgba(0,0,0,0)";
const useMenuItemStyles = makeStyles((theme) => ({
    root: (props: any) => ({
        backgroundColor: props.open ? theme.palette.action.hover : TRANSPARENT,
    }),
}));

export interface INestedMenuItemProps extends Omit<MenuItemProps, "button"> {
    /**
     * Open state of parent `<Menu />`, used to close decendent menus when the
     * root menu is closed.
     */
    parentMenuOpen: boolean;
    /**
     * Component for the container element.
     * @default 'div'
     */
    component?: React.ElementType;
    /**
     * Effectively becomes the `children` prop passed to the `<MenuItem/>`
     * element.
     */
    label?: React.ReactNode;
    /**
     * @default <ChevronRightIcon />
     */
    rightIcon?: React.ReactNode;
    /**
     * Props passed to container element.
     */
    ContainerProps?: React.HTMLAttributes<HTMLElement> & React.RefAttributes<HTMLElement | null>;
    /**
     * Props passed to sub `<Menu/>` element
     */
    MenuProps?: Omit<MenuProps, "children">;
    /**
     * @see https://material-ui.com/api/list-item/
     */
    button?: true | undefined;
    /**
     * Disable the top level menu button
     */
    disabled?: boolean;
    /**
     * Props passed to container as identifier for automation test
     */
    dataTest?: string;
}

/**
 * Use as a drop-in replacement for `<MenuItem>` when you need to add cascading
 * menu elements as children to this component. Altered verion of original make
 * to be compatible with this version of React and Material-UI.
 *
 * @see https://github.com/azmenak/material-ui-nested-menu-item
 *
 * @param parentMenuOpen - Property that indicates whether the parent is open
 * @param component - Component used to render MenuItem
 * @param label - MenuItem label
 * @param rightIcon - Icon that shows nested menus
 * @param children - Nested child menu
 * @param className - MenuItem className
 * @param tabIndex - Index used for keyboard navigation
 * @param MenuProps - Property used to pass to menu
 * @param disabled - Disable top level menu item
 *
 * @returns Menu and menu items
 */
const NestedMenuItem: React.FunctionComponent<INestedMenuItemProps> = (props: INestedMenuItemProps) => {
    const {
        parentMenuOpen,
        component = "div",
        label,
        rightIcon = <ChevronRightIcon />,
        children,
        className,
        tabIndex: tabIndexProp,
        MenuProps = {},
        ContainerProps: ContainerPropsProp = {},
        disabled,
        dataTest,
        // eslint-disable-next-line
        ...MenuItemProps
    } = props;

    const { ref: containerRefProp, ...ContainerProps } = ContainerPropsProp;

    const menuItemRef = React.useRef<HTMLLIElement>(null);
    // React.useImperativeHandle(ref, () => menuItemRef.current)

    const containerRef = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(containerRefProp, () => containerRef.current);

    const menuContainerRef = React.useRef<HTMLDivElement>(null);

    const [isSubMenuOpen, setIsSubMenuOpen] = React.useState(false);

    const onMenuClose = () => {
        setIsSubMenuOpen(false);
    };

    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
        setIsSubMenuOpen(true);

        if (ContainerProps && ContainerProps.onMouseEnter) {
            ContainerProps.onMouseEnter(event);
        }
    };
    const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
        setIsSubMenuOpen(false);

        if (ContainerProps && ContainerProps.onMouseLeave) {
            ContainerProps.onMouseLeave(event);
        }
    };

    // Check if any immediate children are active
    const isSubmenuFocused = () => {
        const active =
            containerRef.current &&
            containerRef.current.ownerDocument &&
            containerRef.current.ownerDocument.activeElement;
        const group = (menuContainerRef.current && menuContainerRef.current.children) || [];
        for (const key in group) {
            if (group.hasOwnProperty(key)) {
                const child = group[key];
                if (child === active) {
                    return true;
                }
            }
        }
        return false;
    };

    const handleFocus = (event: React.FocusEvent<HTMLElement>) => {
        if (event.target === containerRef.current) {
            setIsSubMenuOpen(true);
        }

        if (ContainerProps && ContainerProps.onFocus) {
            ContainerProps.onFocus(event);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape") {
            return;
        }

        if (isSubmenuFocused()) {
            event.stopPropagation();
        }

        const active =
            containerRef.current &&
            containerRef.current.ownerDocument &&
            containerRef.current.ownerDocument.activeElement;

        if (event.key === "ArrowLeft" && isSubmenuFocused() && containerRef.current) {
            containerRef.current.focus();
        }

        if (event.key === "ArrowRight" && event.target === containerRef.current && event.target === active) {
            const firstChild =
                menuContainerRef.current && (menuContainerRef.current.children[0] as HTMLElement | undefined);
            if (firstChild) {
                firstChild.focus();
            }
        }
    };

    const open = isSubMenuOpen && parentMenuOpen;
    const menuItemClasses = useMenuItemStyles({ open });

    // Root element must have a `tabIndex` attribute for keyboard navigation
    let tabIndex;
    if (!props.disabled) {
        tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1;
    }

    return (
        <div
            {...ContainerProps}
            ref={containerRef}
            onFocus={handleFocus}
            tabIndex={tabIndex}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onKeyDown={handleKeyDown}
            data-test={dataTest}
        >
            <MenuItem
                {...MenuItemProps}
                className={clsx(menuItemClasses.root, className)}
                ref={menuItemRef}
                disabled={disabled}
            >
                {label}
                {rightIcon}
            </MenuItem>
            {!disabled && ( // suppress menu display if disabled
                <Menu
                    // Set pointer events to 'none' to prevent the invisible Popover div
                    // from capturing events for clicks and hovers
                    style={{ pointerEvents: "none" }}
                    anchorEl={menuItemRef.current}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    open={open}
                    autoFocus={false}
                    disableAutoFocus={true}
                    disableEnforceFocus={true}
                    onClick={onMenuClose} // onClose does not work since nested menu does not exit
                >
                    <div ref={menuContainerRef} style={{ pointerEvents: "auto" }}>
                        {children}
                    </div>
                </Menu>
            )}
        </div>
    );
};

export default NestedMenuItem;
